import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { usePhotoGalleryStore } from 'stores/usePhotoGalleryStore';
import styled from 'styled-components';

import DynamicImport from 'components/core/DynamicImport';
import NoConnection from 'components/core/errors/NoConnection';
import GlobalNav from 'components/core/navigation/GlobalNav';
import { ElementTestId } from 'enums/testing';
import { useGlobalDialog } from 'hooks/contexts/useGlobalDialog';
import { impersonationManager } from 'utils/impersonationUtils';

import { DIVIDER } from 'styles/color';
import { BREAKPOINTS } from 'styles/layouts';
import { HEIGHT_800 } from 'styles/tokens';

import PromptDialog from '../dialogs/PromptDialog';
import PhotoPreviewsLoadingOverlay from './PhotoPreviewsLoadingOverlay';

const CreateModifyContainer = DynamicImport(() => import('containers/createModify/CreateModifyContainer'), null);
const PhotoPreviewsContainer = DynamicImport(
  () => import('containers/photoPreviewsContainer/PhotoPreviewsContainer'),
  <PhotoPreviewsLoadingOverlay />
);
const SubscribedNotifications = DynamicImport(
  () => import('components/sections/notifications/SubscribedNotifications'),
  null
);

export const CoreLayoutContainer = styled.div<{ $isBannerVisible: boolean }>`
  background: ${DIVIDER};
  display: flex;
  flex-direction: row;
  height: ${({ $isBannerVisible }) =>
    /**
     * HEIGHT_800 is the height of the full-width `Logged In As` (impersonation) top banner. While it
     * is visible we subtract its height here to prevent the layout shifting off screen.
     */
    $isBannerVisible ? `calc(100vh - ${HEIGHT_800})` : '100vh'};
  min-width: ${BREAKPOINTS.LARGE};
  width: 100%;
`;

export const CoreLayoutContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;

  > * {
    flex-shrink: 0;
  }
`;

interface Props {
  children: ReactNode;
}

const CoreLayout = ({ children }: Props) => {
  const [isConnected, setIsConnected] = useState(navigator.onLine);
  const isGalleryOpen = usePhotoGalleryStore(state => state.isOpen);
  const [hasGalleryOpened, setHasGalleryOpened] = useState(false);
  const { config, setConfig } = useGlobalDialog();

  const connectionCallback = useCallback(event => setIsConnected(event.type === 'online'), [setIsConnected]);
  const onDialogComplete = useCallback(
    success => {
      setConfig();
      void config?.onComplete?.(success);
    },
    [config, setConfig]
  );

  if (isGalleryOpen && !hasGalleryOpened) {
    setHasGalleryOpened(true);
  }

  useEffect(() => {
    globalThis.addEventListener('online', connectionCallback);
    globalThis.addEventListener('offline', connectionCallback);

    return () => {
      globalThis.removeEventListener('online', connectionCallback);
      globalThis.removeEventListener('offline', connectionCallback);
    };
  }, [connectionCallback]);

  return isConnected ? (
    <>
      <CoreLayoutContainer
        $isBannerVisible={impersonationManager.isCurrentlyImpersonating}
        data-testid={ElementTestId.CORE_LAYOUT_CONTAINER}
      >
        <GlobalNav />
        <CoreLayoutContentContainer>{children}</CoreLayoutContentContainer>
        <CreateModifyContainer />
        {hasGalleryOpened ? <PhotoPreviewsContainer /> : null}
      </CoreLayoutContainer>
      <SubscribedNotifications />
      <PromptDialog {...config} isOpen={!!config} onComplete={onDialogComplete} />
    </>
  ) : (
    <NoConnection />
  );
};

export default CoreLayout;
